import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@gatsbyjs/reach-router'
import { Button, Checkbox, message, Modal } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import routes from 'routes/routes'
import { useAuth } from 'store/authStore/authStore'

import {
    InnerWrapper,
    List,
    ModalWrapper,
} from './TermsAndConditionsModal.styles'

function TermsAndConditionsModal() {
    const [open, setOpen] = useState(false)
    const { t } = useTranslation()
    const [{ user }, { logOut, acceptTermsAndConditions, checkAcceptance }] =
        useAuth()
    const [gdprAccepted, setGdprAccepted] = useState(false)
    const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] =
        useState(false)
    const location = useLocation()
    const [messageApi, contextHolder] = message.useMessage()

    const errorMessage = () => {
        messageApi.open({ type: 'error', content: t('terms_acceptance_error') })
    }

    const excludedRoutes = [
        routes.termsAndConditions,
        routes.aboutLogin,
        routes.gdprPrivacyNote,
        routes.termsOfUse,
        routes.privacyPolicy,
    ]

    const showModal = () => {
        setOpen(true)
    }

    const hideModal = () => {
        setOpen(false)
    }

    useEffect(() => {
        checkAcceptance()
    }, [])

    useEffect(() => {
        const areTermsAccepted =
            user.gdprAccepted && user.termsAndConditionsAccepted
        const isExcludedRoute = excludedRoutes.some((route) =>
            location.pathname.includes(route)
        )

        if (!user.isAcceptanceChecked || areTermsAccepted || isExcludedRoute) {
            hideModal()
        } else {
            showModal()
        }
    }, [
        user.gdprAccepted,
        user.termsAndConditionsAccepted,
        location.pathname,
        user.isAcceptanceChecked,
    ])

    const handleChange =
        (setState: Dispatch<SetStateAction<boolean>>) =>
        (event: CheckboxChangeEvent) => {
            setState(event.target.checked)
        }

    return (
        <Modal
            open={open}
            title={t('terms_and_conditions')}
            closable={false}
            footer={() => (
                <>
                    <Button onClick={logOut}>{t('i_do_not_accept')}</Button>
                    <Button
                        type="primary"
                        onClick={() =>
                            acceptTermsAndConditions(
                                {
                                    gdprAccepted,
                                    termsAndConditionsAccepted,
                                },
                                errorMessage
                            )
                        }
                        disabled={!(gdprAccepted && termsAndConditionsAccepted)}
                    >
                        {t('i_accept')}
                    </Button>
                </>
            )}
        >
            <ModalWrapper>
                {contextHolder}
                <p>{t('accept_terms_to_continue')}</p>
                <InnerWrapper>
                    <Checkbox
                        onChange={handleChange(setTermsAndConditionsAccepted)}
                    >
                        {t('i_accept')}{' '}
                        <a
                            href={routes.termsOfUse}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('terms_of_use')}
                        </a>
                    </Checkbox>
                    <Checkbox onChange={handleChange(setGdprAccepted)}>
                        {t('gdpr_checkbox')}
                    </Checkbox>
                    <p>
                        {t('following_data_will_be_processed')}
                        <List>
                            <li>{t('name_and_surname')};</li>
                            <li>{t('email_address')};</li>
                            <li>{t('encrypted_password')};</li>
                            <li>{t('ip_address')};</li>
                            <li>{t('affiliation')};</li>
                        </List>
                    </p>

                    <a
                        href={routes.gdprPrivacyNote}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('about_data_processing')}
                    </a>
                    <a
                        href={routes.aboutLogin}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('about_login')}
                    </a>
                    <a
                        href={routes.termsOfUse}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('terms_of_use')}
                    </a>

                    <p>{t('above_consents_required')}</p>
                </InnerWrapper>
            </ModalWrapper>
        </Modal>
    )
}

export default TermsAndConditionsModal
